import type { ApiResponse } from '@/types/apiResponse';
import type {
  CareerFitPurchaseResult,
  CareerFitResult,
  CareerFitSubmitResult,
} from '@/types/academy/careerFit';
import { apiService } from '../api';

class CareerFitService {
  private static instance: CareerFitService;

  private constructor() {}

  public static getInstance(): CareerFitService {
    if (!CareerFitService.instance) {
      CareerFitService.instance = new CareerFitService();
    }
    return CareerFitService.instance;
  }

  /**
   * Fetches the Career Fit Check questionnaire and track metadata.
   * GET /private/api/v1/career-fit — authenticated, because only signed-in
   * users can take the assessment and the payload carries the scoring key.
   */
  async getCareerFit(): Promise<ApiResponse<CareerFitResult>> {
    return apiService.get<ApiResponse<CareerFitResult>>(
      '/private/api/v1/career-fit',
    );
  }

  /**
   * The user's most recent submitted assessment, or `null` when they have never
   * taken one. GET /private/api/v1/career-fit/latest
   */
  async getLatestResult(): Promise<ApiResponse<CareerFitSubmitResult | null>> {
    return apiService.get<ApiResponse<CareerFitSubmitResult | null>>(
      '/private/api/v1/career-fit/latest',
    );
  }

  /**
   * Submits the selected option ids in question order. The server scores them,
   * stores the attempt, and returns the track plus the module to start with.
   * POST /private/api/v1/career-fit/submit — 403 when attempts are exhausted.
   */
  async submitAnswers(
    optionIds: string[],
  ): Promise<ApiResponse<CareerFitSubmitResult>> {
    return apiService.post<ApiResponse<CareerFitSubmitResult>>(
      '/private/api/v1/career-fit/submit',
      { answers: optionIds },
    );
  }

  /**
   * Buys one extra assessment attempt for EC.
   * POST /private/api/v1/career-fit/purchase
   */
  async purchaseRetest(): Promise<ApiResponse<CareerFitPurchaseResult>> {
    return apiService.post<ApiResponse<CareerFitPurchaseResult>>(
      '/private/api/v1/career-fit/purchase',
      {},
    );
  }
}

export const careerFitService = CareerFitService.getInstance();
